/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import type { TInputEvent } from "./types/TInputEvent";
import type { IScreenController } from "./interfaces/IScreenController";

/**
 * Tracks all registered ScreenControllers and routes input
 * to the currently active screen (determined by Redux state).
 */
export class ScreenRegistry {
  private static TAG: string = "ScreenRegistry";

  private mControllers: Map<string, IScreenController> = new Map();

  public registerScreen(controller: IScreenController): void {
    if (this.mControllers.has(controller.screenName)) {
      Log.w(
        ScreenRegistry.TAG,
        `registerScreen() — "${controller.screenName}" already registered, replacing`
      );
    }
    this.mControllers.set(controller.screenName, controller);
  }

  public unregisterScreen(name: string): void {
    const controller = this.mControllers.get(name);
    if (controller) {
      controller.cleanup();
      this.mControllers.delete(name);
    }
  }

  public getController(name: string): IScreenController | undefined {
    return this.mControllers.get(name);
  }

  public routeInput(activeScreen: string, event: TInputEvent): boolean {
    const controller = this.mControllers.get(activeScreen);
    if (!controller) {
      Log.w(
        ScreenRegistry.TAG,
        `routeInput() — no controller registered for "${activeScreen}"`
      );
      return false;
    }
    return controller.handleInput(event);
  }

  public getRegisteredScreens(): string[] {
    return Array.from(this.mControllers.keys());
  }

  public cleanup(): void {
    for (const controller of this.mControllers.values()) {
      controller.cleanup();
    }
    this.mControllers.clear();
  }
}
